import { addMonths, addWeeks } from 'date-fns';

import { optimisticTranslate } from '@i18n';

import { locales } from '@framework/utils/common';

import { AGENDA_CATEGORIES } from '@meetings/details/agenda/agenda.constants';

import { type TaskDecisionType } from '@tasksDecisions/tasksDecisions.types';

import { type CalendarPanelItemType } from 'styled-system';

export const MAX_LENGTH_MEETING_NAME = 100;
export const MAX_LENGTH_LOCATION = 250;
export const MAX_LENGTH_CONFERENCE_CALL = 1000;
export const MAX_LENGTH_PURPOSE_OF_MEETING = 10000;
export const MAX_LENGTH_EMAIL_SUBJECT = 200;
export const CHARS_LEFT_SHOW_COUNTER = 10;
export const ONBOARDING_MINUTES = 'onboarding_minutes';
export const DISABLE_BOARDBOOK_DEFAULT_MODAL = 'disable_boardbook_default_modal';
export const FEEDBACK_FORM_TOGGLE_DEFAULT_VALUE = 'feedback_form_toggle_default_value';
export const CANCEL_CHECK_IN_SUGGESTION_MODAL = 'cancel_check_in_suggestion_modal';
export const DELETE_GENERATED_CHECK_IN_AGENDA_ITEM = 'delete_generated_check_in_agenda_item';
export const CHANGE_DOCUMENT_PRE_READING_FLAG_VALUE = 'change_document_pre_reading_flag_value';
export const MINUTES_FEATURE_REQUESTED = 'minutes_feature_requested';
export const DEFAULT_SIGNATURE_TYPE = (loggedUserId: string) =>
    `default_signature_type_${loggedUserId}`;
export const DEFAULT_SIGNATURE_TYPE_COUNTER_THRESHOLD = 3;
export const GENERATING_SIGNATURE_REQUEST_SNACKBAR = 'generating_signature_request_snackbar';
export const STOPPING_SIGNATURE_REQUEST_SNACKBAR = 'stopping_signature_request_snackbar';

export const INPUT_CHAR_REGEX_WHITELIST =
    /^[0-9a-zA-ZÀ-ÿæœŸÆŒąćęłńźżĄĆĘŁŃŚŹŻ^!?^\-‐–_"()[\]’:,.'°º®€£&§@#%≤≥+|¦/„“”‚‘«»\s]*$/gim;
export const BACKSLASH_REGEX = /\\/;

export const MINIMUM_MEETING_DURATION_MINUTES = 15;
export const INVALID_MEETING_START_TIME = {
    HOURS: 23,
    MINUTES: MINIMUM_MEETING_DURATION_MINUTES + 1
};
export const INVALID_MEETING_END_TIME = {
    HOURS: 0,
    MINUTES: MINIMUM_MEETING_DURATION_MINUTES - 1
};

export const LOCALES: { [key: string]: Locale } = locales;

export const EVENT_TYPES: {
    PHYSICAL: number;
    CONFERENCE_CALL: number;
    CIRCULAR_RESOLUTION: number;
} = {
    PHYSICAL: 1,
    CONFERENCE_CALL: 2,
    CIRCULAR_RESOLUTION: 3
};

export const AGENDA_COLORS: {
    [key: number]: string;
} = {
    [AGENDA_CATEGORIES.APPROVAL]: 'var(--color-brand-agenda-approval-surface)',
    [AGENDA_CATEGORIES.INFORMATION]: 'var(--color-brand-agenda-information-surface)',
    [AGENDA_CATEGORIES.DISCUSSION]: 'var(--color-brand-agenda-discussion-surface)',
    [AGENDA_CATEGORIES.WORKSHOP]: 'var(--color-brand-agenda-workshop-surface)',
    [AGENDA_CATEGORIES.ORGANISATIONAL]: 'var(--color-brand-agenda-org-surface)',
    [AGENDA_CATEGORIES.APPROVAL_WITH_VOTING]: 'var(--color-brand-agenda-approval-surface)'
};

export const DETAILS_TABS: {
    AGENDA: string;
    DETAILS: string;
    HISTORY: string;
    MINUTES: string;
    MINUTES_EDITOR: string;
    NOTIFICATIONS: string;
} = {
    AGENDA: 'agenda',
    DETAILS: 'details',
    HISTORY: 'history',
    MINUTES: 'minutes',
    MINUTES_EDITOR: 'minutes-editor',
    NOTIFICATIONS: 'notifications'
};

export const getOmittedValues = (isNew: boolean): string[] => [
    'agendaActionLoading',
    'agendaActionType',
    'agendaEnabled',
    'agendaLastModifiedAt',
    'agendaTemplates',
    'attendance',
    'canEdit',
    'commentsCount',
    'contributionRequestModalVisibility',
    'coverPageEnabled',
    'defaultSecurity',
    'documents',
    'endTime',
    'expandedMinutesItem',
    'invitationDocument',
    'invitationDocumentCreated',
    'invitationDocumentName',
    'invitationThumbnail',
    'invitationUploaded',
    'membersSmartList',
    'minutes',
    'outdatedAgendaModalVisible',
    'showAgendaTemplates',
    'startTime',
    'topics',
    'ui',
    ...(isNew ? ['defaultDuration'] : [])
];

export const getInitialMeetingValues = (isNew: boolean, isClonedMeeting: boolean): string[] => [
    'conferenceCall',
    'conferenceCallProvider',
    'endTime',
    'groups',
    'information',
    'location',
    'meetingFeedbackForm',
    'meetingLead',
    'members',
    'msteamsEvent',
    'name',
    'owner',
    'publishOn',
    'startTime',
    'timezone',
    'ui',
    ...(!isClonedMeeting ? ['start', 'end', 'deadline'] : []),
    ...(!isNew ? ['defaultDuration'] : [])
];

export const LIMIT_FOR_SUGGESTED_MEMBERS = 4;

export const NEW_MEETING_UUID = 'new';
export const HISTORY_SLUG = 'history';

export const RESOURCE_NOT_FOUND = 'resource-not-found';
export const ACCESS_DENIED = 'access-denied';

export const MAX_DOCUSIGN_SIGNERS_COUNT = 99;

export const getMinutesDateTemplates = (type: TaskDecisionType): CalendarPanelItemType[] => [
    {
        title:
            type === 'task'
                ? optimisticTranslate('no due date')
                : optimisticTranslate('no review date'),
        value: null
    },
    {
        title: optimisticTranslate('1 week'),
        value: addWeeks(new Date(), 1)
    },
    {
        title: optimisticTranslate('{{ weeksCount }} weeks', {
            weeksCount: 2
        }),
        value: addWeeks(new Date(), 2)
    },
    {
        title: optimisticTranslate('1 month'),
        value: addMonths(new Date(), 1)
    },
    {
        title: optimisticTranslate('{{ monthsCount }} months', {
            monthsCount: 3
        }),
        value: addMonths(new Date(), 3)
    }
];

export const SUPPORT_CENTRE_LINKS: { [language: string]: string } = {
    'de-CH': 'https://support.sherpany.com/de/support/solutions/articles/3000127992-gast-lizenz',
    'en-GB': 'https://support.sherpany.com/en/support/solutions/articles/3000127992-guest-licence',
    'fr-FR':
        'https://support.sherpany.com/fr/support/solutions/articles/3000127992-la-licence-visiteurs',
    'it-IT': 'https://support.sherpany.com/it/support/solutions/articles/3000127992-licenza-ospite'
};

export const COVER_PAGE_URL = '/static/pdf_examples/coverpage.pdf';
export const AGENDA_URL = '/static/pdf_examples/agenda.pdf';
